import { Controller } from "@hotwired/stimulus"

// swipe-actions
// ---------------------------------------------------------------
// iOS-native swipe-to-reveal actions on a list row.
//
// Layout expected on the row:
//   <div data-controller="swipe-actions" ...>
//     <div data-swipe-actions-target="startRail">[Skip button]</div>  ← revealed when swiping toward the end
//     <div data-swipe-actions-target="endRail">[Stop button]</div>    ← revealed when swiping toward the start
//     <div data-swipe-actions-target="content">row body</div>
//   </div>
//
// Behavior:
//   • Pointer-based drag (mouse + touch) with rubber-band past max
//   • Flick velocity OR threshold (45% of rail width) commits to open
//   • Tap outside any open row closes it
//   • Opening a row closes any other open row
//   • RTL-correct — signs flip so "start" is always the reading start
//   • First-ever view runs a one-time peek-and-bounce hint teaching
//     the gesture (localStorage flag). Respects prefers-reduced-motion.
//   • When a row is open, tapping its content CLOSES it (no navigation)
//   • Tap on a revealed action button submits its nested form

const OPEN_ROWS = (() => {
  if (typeof window === "undefined") return new Set()
  return window.__swipeActionsOpen ||= new Set()
})()

function closeAll(except) {
  OPEN_ROWS.forEach((r) => { if (r !== except) r.__close?.() })
}

const HINT_KEY_DEFAULT = "floosyo.due-today-swipe-hint.v1"

export default class extends Controller {
  static targets = ["content", "startRail", "endRail"]
  static values = {
    hintKey:    { type: String, default: HINT_KEY_DEFAULT },
    hintDelay:  { type: Number, default: 500 },
    autoPlayHint: { type: Boolean, default: true },
    threshold:  { type: Number, default: 0.45 },
    flickVelocity: { type: Number, default: 0.6 },
    rubberResist:  { type: Number, default: 0.35 }
  }

  get reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  // LTR: "start" rail sits on the left, revealed by dragging RIGHT (positive X).
  //      "end"   rail sits on the right, revealed by dragging LEFT (negative X).
  // RTL: flipped.
  get _dir() {
    return document.documentElement.dir === "rtl" ? -1 : 1
  }

  connect() {
    // pan-y: the browser owns vertical scrolling over the row; only
    // horizontal movement reaches the drag logic below.
    this.element.style.touchAction = "pan-y"

    this._state = 0
    this._currentX = 0
    this._dragging = false
    this._dragged = false
    this._startX = 0
    this._lastX = 0
    this._lastT = 0
    this._vel = 0
    this._maxes = { maxPositive: 0, maxNegative: 0 }
    this.element.__close = () => this.close()

    this._onPointerDown = this._onPointerDown.bind(this)
    this._onPointerMove = this._onPointerMove.bind(this)
    this._onPointerUp   = this._onPointerUp.bind(this)
    this._onDocDown     = this._onDocDown.bind(this)
    this._onClickCapture = this._onClickCapture.bind(this)

    this.element.addEventListener("pointerdown", this._onPointerDown)
    this.element.addEventListener("pointermove", this._onPointerMove)
    this.element.addEventListener("pointerup",   this._onPointerUp)
    this.element.addEventListener("pointercancel", this._onPointerUp)
    this.element.addEventListener("click",       this._onClickCapture, true)
    document.addEventListener("pointerdown",     this._onDocDown)

    // Snap shut with no animation before Turbo caches the page — otherwise
    // the snapshot freezes a mid-swipe transform and the restored preview
    // shows rails open / content shifted.
    this._onBeforeCache = () => {
      this._state = 0
      this.element.dataset.swipeOpen = "false"
      if (this.hasContentTarget) {
        this.contentTarget.style.transition = "none"
        this.contentTarget.style.transform = ""
        this.contentTarget.style.willChange = ""
      }
      OPEN_ROWS.delete(this.element)
    }
    document.addEventListener("turbo:before-cache", this._onBeforeCache)

    // Wire inner action forms/buttons: when the form submits, also
    // close the row so any animation sequence (like skip celebration)
    // starts from a clean state.
    this.element.querySelectorAll("form").forEach((f) => {
      f.addEventListener("submit", () => this.close(), { once: false })
    })

    // Auto-play the peek hint once per user (scoped via localStorage).
    if (this.autoPlayHintValue && !this._hintSeen() && !this.reducedMotion) {
      window.setTimeout(() => this.playHint(), this.hintDelayValue)
      this._markHintSeen()
    }
  }

  disconnect() {
    this.element.removeEventListener("pointerdown", this._onPointerDown)
    this.element.removeEventListener("pointermove", this._onPointerMove)
    this.element.removeEventListener("pointerup",   this._onPointerUp)
    this.element.removeEventListener("pointercancel", this._onPointerUp)
    this.element.removeEventListener("click",       this._onClickCapture, true)
    document.removeEventListener("pointerdown",     this._onDocDown)
    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    OPEN_ROWS.delete(this.element)
    this.element.__close = null
  }

  // ── Hint persistence ─────────────────────────────────────────────
  // sessionStorage (not localStorage): the hint plays on each fresh
  // app session so returning users are re-educated, but not on every
  // navigation within the same session.
  _hintSeen() {
    try { return !!window.sessionStorage.getItem(this.hintKeyValue) }
    catch (e) { return false }
  }

  _markHintSeen() {
    try { window.sessionStorage.setItem(this.hintKeyValue, Date.now().toString()) }
    catch (e) { /* private mode — skip */ }
  }

  // ── Measurements ─────────────────────────────────────────────────
  _measure() {
    const startW = this.hasStartRailTarget ? this.startRailTarget.getBoundingClientRect().width : 0
    const endW   = this.hasEndRailTarget   ? this.endRailTarget.getBoundingClientRect().width   : 0
    const dir = this._dir
    // translateX sign that reveals the start rail: +1 in LTR, -1 in RTL.
    // Conversely, revealing the end rail goes opposite.
    return {
      maxPositive: dir === 1 ? startW : endW,
      maxNegative: dir === 1 ? endW   : startW
    }
  }

  // ── Pointer handlers ─────────────────────────────────────────────
  _onPointerDown(e) {
    // ALWAYS reset the "was a drag" flag on a new gesture. On mobile a
    // finished swipe never produces a click to clean it up, so without
    // this, the next genuine tap (e.g. on Skip) gets swallowed.
    this._dragged = false

    // Don't start a swipe on an action button — let the button handle the tap.
    if (e.target.closest("[data-swipe-action-button]")) return
    // Promote the content to its own layer only while a gesture is live —
    // a standing will-change on every row makes iOS WKWebView flicker and
    // paint blank rows on long scrolling lists.
    if (this.hasContentTarget) this.contentTarget.style.willChange = "transform"
    this._dragging = true
    this._committed = false
    this._startX = e.clientX
    this._startY = e.clientY
    this._lastX = e.clientX
    this._lastT = performance.now()
    this._startState = this._state
    this._vel = 0
    this._maxes = this._measure()
    this.element.dataset.swiping = "true"
    this.element.setPointerCapture?.(e.pointerId)
  }

  _onPointerMove(e) {
    if (!this._dragging) return

    // Direction commit (same pattern as confirm_sheet dragMove): decide
    // once whether this gesture is a horizontal swipe or a vertical
    // scroll. Vertical-dominant movement releases the gesture entirely,
    // so page scrolling never jiggles or snaps the rails open.
    if (!this._committed) {
      const cdx = e.clientX - this._startX
      const cdy = e.clientY - this._startY
      if (Math.abs(cdx) < 6 && Math.abs(cdy) < 6) return
      if (Math.abs(cdy) > Math.abs(cdx)) {
        this._dragging = false
        this.element.dataset.swiping = "false"
        this._applyTransform(this._startState, true)
        return
      }
      this._committed = true
    }

    const now = performance.now()
    const dx = e.clientX - this._startX
    const dt = Math.max(1, now - this._lastT)
    this._vel = (e.clientX - this._lastX) / dt
    this._lastX = e.clientX
    this._lastT = now

    let target = this._startState + dx
    if (target > this._maxes.maxPositive) {
      target = this._maxes.maxPositive + (target - this._maxes.maxPositive) * this.rubberResistValue
    } else if (target < -this._maxes.maxNegative) {
      target = -this._maxes.maxNegative - (-target - this._maxes.maxNegative) * this.rubberResistValue
    }

    // iOS-style touch slop: a tap isn't promoted to a drag until the
    // finger moves more than ~10px, so natural finger jitter during a
    // simple tap doesn't swallow the click on the row's link.
    if (Math.abs(dx) > 10) this._dragged = true
    this._applyTransform(target)
  }

  _onPointerUp(e) {
    if (!this._dragging) return
    this._dragging = false
    this.element.dataset.swiping = "false"
    this.element.releasePointerCapture?.(e.pointerId)

    const current = this._readTransform()
    let snap = 0
    if (current > 0 &&
        (Math.abs(this._vel) > this.flickVelocityValue ||
         current > this._maxes.maxPositive * this.thresholdValue)) {
      snap = this._maxes.maxPositive
    } else if (current < 0 &&
        (Math.abs(this._vel) > this.flickVelocityValue ||
         -current > this._maxes.maxNegative * this.thresholdValue)) {
      snap = -this._maxes.maxNegative
    }

    if (snap === 0) {
      this.close()
    } else {
      closeAll(this.element)
      this.open(snap)
    }
  }

  _onDocDown(e) {
    if (this._state === 0) return
    if (this.element.contains(e.target)) return
    this.close()
  }

  // If a drag actually moved the row (mouse drag on desktop), swallow
  // the synthesized click so links don't open by accident.
  //
  // At rest (closed row) a normal tap ALWAYS propagates to the
  // link_to wrapping the row content, so the expense detail opens
  // just like before the swipe feature existed.
  _onClickCapture(e) {
    if (this._dragged) {
      e.preventDefault()
      e.stopPropagation()
      this._dragged = false
    }
  }

  // ── State transitions ────────────────────────────────────────────
  open(target) {
    this._state = target
    this.element.dataset.swipeOpen = "true"
    this._applyTransform(target, true)
    OPEN_ROWS.add(this.element)
  }

  close() {
    this._state = 0
    this.element.dataset.swipeOpen = "false"
    this._applyTransform(0, true)
    OPEN_ROWS.delete(this.element)
    // Drop the gesture-scoped GPU layer once the close animation settles.
    if (this.hasContentTarget) {
      window.clearTimeout(this._willChangeTimer)
      this._willChangeTimer = window.setTimeout(() => {
        if (this._state === 0 && this.hasContentTarget) this.contentTarget.style.willChange = ""
      }, 300)
    }
  }

  // ── Transform helpers ────────────────────────────────────────────
  // Option A behavior: instead of translating the content and clipping
  // it against the row's overflow-hidden, we shrink its width by
  // pushing its physical left/right margins outward. The card stays
  // fully visible (just narrower) and the action rail fills the gap.
  // At rest both margins are 0: the content covers the row edge to edge,
  // and the Skip/Stop edge lines are its own inset shadow
  // (.due-today-content), not a sliver of rail showing past it.
  _applyTransform(x, withTransition = false) {
    if (!this.hasContentTarget) return
    const c = this.contentTarget
    c.style.transition = withTransition
      ? "margin 320ms cubic-bezier(0.32, 0.72, 0, 1)"
      : "none"
    c.style.transform = "none"
    if (x >= 0) {
      c.style.marginLeft  = `${x}px`
      c.style.marginRight = "0px"
    } else {
      c.style.marginLeft  = "0px"
      c.style.marginRight = `${-x}px`
    }
    this._currentX = x
  }

  _readTransform() {
    return this._currentX || 0
  }

  // ── Auto-peek hint ───────────────────────────────────────────────
  // Premium choreographed teach: reveals ~88% of the start-edge
  // button (Skip), HOLDS long enough for the user to read it, returns
  // smoothly to center with a brief composed pause, reveals the
  // end-edge button (Stop), holds, returns. Feels deliberate, not
  // "boingy".
  //
  // Timeline (total ≈ 3.2s):
  //    0 → 500    slide to start peek    (silky ease-out)
  //  500 → 980    hold · reveals Skip   (∼480ms read time)
  //  980 → 1460   settle back to center (balanced ease-in-out)
  // 1460 → 1660   composed pause
  // 1660 → 2160   slide to end peek
  // 2160 → 2640   hold · reveals Stop
  // 2640 → 3200   settle back to center
  // ── Peek-hint choreography ───────────────────────────────────────
  // Tuned to iOS Mail / Things 3 quality:
  //   • Pure deceleration curves only (no bounce, no overshoot).
  //   • Exits ~75% of entrance duration ("decisive return").
  //   • Holds are dead-still — no drift, no breath. Stillness reads
  //     premium; micro-jitter reads jittery.
  //   • Rail accents (opacity + scale) sync to the row's reveal, fade
  //     out one beat before the row returns to center.
  //   • A whisper-soft elevation lifts the whole row during each peek.
  //
  // Total timeline ≈ 2400ms:
  //    0   →  320   reveal Skip       (ease-out-expo)
  //  320   →  920   hold Skip         (linear, 600ms)
  //  920   → 1160   return to center  (ease-out-quart, ~75% of enter)
  // 1160   → 1320   composed pause
  // 1320   → 1640   reveal Stop
  // 1640   → 2240   hold Stop
  // 2240   → 2400   return to center  (ease-out-quart)
  // Tug-tease: rubber-band physics that hints the row is grabbable
  // WITHOUT revealing the Skip/Stop buttons. Content pulls against
  // resistance, holds, then snaps back with critical damping. Mirrors
  // on the other side.
  //
  // Timeline (total = 1800ms):
  //    0   →  360   tug out toward start  (ease-in resist)
  //  360   →  480   hold at stretched point
  //  480   →  800   snap back to center   (ease-out-quart, no bounce)
  //  800   → 1000   composed pause
  // 1000   → 1360   tug out toward end    (ease-in resist)
  // 1360   → 1480   hold at stretched point
  // 1480   → 1800   snap back to center   (ease-out-quart)
  playHint() {
    if (!this.hasContentTarget) return
    const m = this._measure()

    // Tug distance — small (22px), capped by available rail width so
    // we never accidentally expose the action.
    const tug = 22
    const pos =  Math.min(tug, m.maxPositive)
    const neg = -Math.min(tug, m.maxNegative)

    const duration = 1800

    // Resistance going out, refined return on the snap. No bounce.
    const resist = "cubic-bezier(0.55, 0, 0.30, 1)" // ease-in resistance
    const quart  = "cubic-bezier(0.25, 1, 0.5, 1)"  // ease-out-quart
    const stay   = "linear"

    const O = {
      start:        0.000,
      tugStartEnd:  0.200,   //  360ms
      hold1End:     0.267,   //  480ms
      back1End:     0.444,   //  800ms
      pause1End:    0.556,   // 1000ms
      tugEndEnd:    0.756,   // 1360ms
      hold2End:     0.822,   // 1480ms
      back2End:     1.000    // 1800ms
    }

    const center  = { marginLeft: "0px", marginRight: "0px" }
    const tugLeft = { marginLeft: `${pos}px`, marginRight: "0px" }
    const tugRight= { marginLeft: "0px", marginRight: `${-neg}px` }

    // The rails and the rail-coloured row only exist while the content moves
    // (.due-today-swipe-row in application.css); the hint moves it too.
    this.element.dataset.swiping = "true"
    const hint = this.contentTarget.animate(
      [
        { offset: O.start,       ...center,   easing: resist },
        { offset: O.tugStartEnd, ...tugLeft,  easing: stay   },
        { offset: O.hold1End,    ...tugLeft,  easing: quart  },
        { offset: O.back1End,    ...center,   easing: stay   },
        { offset: O.pause1End,   ...center,   easing: resist },
        { offset: O.tugEndEnd,   ...tugRight, easing: stay   },
        { offset: O.hold2End,    ...tugRight, easing: quart  },
        { offset: O.back2End,    ...center }
      ],
      { duration, fill: "none" }
    )
    hint.finished.then(() => { this.element.dataset.swiping = "false" }).catch(() => {})
  }

  // Rail button accent: fades in as the row reveals it, then fades out
  // a beat before the row returns. Opacity + transform only —
  // GPU-cheap, never touches layout. No blur (jank-prone on iOS WK).
  _railAccent(side, duration, O) {
    const rail = side === "start"
      ? (this.hasStartRailTarget ? this.startRailTarget : null)
      : (this.hasEndRailTarget   ? this.endRailTarget   : null)
    if (!rail) return
    const btn = rail.querySelector("button, a, [data-swipe-action-button]")
    if (!btn) return

    const enter = "cubic-bezier(0.16, 1, 0.3, 1)"
    const exit  = "cubic-bezier(0.25, 1, 0.5, 1)"

    const isStart = side === "start"
    const wakeStart = isStart ? O.start         : O.pauseEnd
    const peak      = isStart ? O.skipReveal    : O.stopReveal
    const holdEnd   = isStart ? O.skipHoldEnd   : O.stopHoldEnd
    const rest      = isStart ? O.backToCenterA : O.backToCenterB

    const stateRest = { opacity: 0.55, transform: "scale(0.94)" }
    const statePeak = { opacity: 1.00, transform: "scale(1.00)" }
    const stateFade = { opacity: 0.55, transform: "scale(0.96)" }

    btn.animate(
      [
        { offset: 0.000,     ...stateRest },
        { offset: wakeStart, ...stateRest, easing: enter },
        { offset: peak,      ...statePeak, easing: enter },
        { offset: holdEnd,   ...statePeak, easing: exit  },
        { offset: rest,      ...stateFade, easing: exit  },
        { offset: 1.000,     ...stateRest }
      ],
      { duration, fill: "none" }
    )
  }

}
